/**
 * IcpMonitor Page
 *
 * Monitor Internet Computer neurons (staking) and accounts.
 * - Neuron IDs: numeric strings (e.g., 12345678901234567890)
 * - Account IDs: 64 hex characters for balance tracking
 */

import { useState } from 'react';
import { Icons } from '../components/Icons';
import { useIcpData, parseIcpInput } from '../hooks/useIcpWallet';
import { useIcpMonitorWallets } from '../hooks/useMonitorWallets';
import { isValidNeuronId, isValidIcpAccountId } from '../services/icpApi';
import { useToast } from '../components/Toast';
import { IcpHealthMetrics } from '../components/icp/IcpHealthMetrics';
import { IcpSupplyBreakdown } from '../components/icp/IcpSupplyBreakdown';
import {
  NetworkMonitorPageHeader,
  NetworkMonitorEmptyState,
  MonitorTabs,
} from '../components/monitor';
import type { ICPNeuronSummary } from '../types/icp.types';

const ICP_COLORS = {
  primary: '#29ABE2',
  secondary: '#522785',
  accent: '#F15A24',
  gradient: 'linear-gradient(135deg, #29ABE2, #522785)',
  lightGradient: 'linear-gradient(135deg, rgba(41, 171, 226, 0.15), rgba(82, 39, 133, 0.1))',
  success: '#10b981',
  warning: '#f59e0b',
};

type TabType = 'neurons' | 'accounts';

const s = {
  page: { padding: '0', maxWidth: '1400px', margin: '0 auto', paddingBottom: '40px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', gap: '20px', flexWrap: 'wrap' as const },
  title: { display: 'flex', alignItems: 'center', gap: '12px' },
  titleText: { fontSize: '24px', fontWeight: 700, margin: 0 },
  subtitle: { fontSize: '13px', color: 'var(--muted)', marginTop: '4px' },
  card: { background: 'rgba(0, 0, 0, 0.25)', backdropFilter: 'blur(20px)', borderRadius: '16px', border: '1px solid rgba(255, 255, 255, 0.08)', padding: '20px', marginBottom: '20px' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', marginBottom: '24px' },
  statCard: (color: string) => ({ background: `${color}10`, border: `1px solid ${color}30`, borderRadius: '12px', padding: '16px', textAlign: 'center' as const }),
  statValue: { fontSize: '22px', fontWeight: 700 },
  statLabel: { fontSize: '11px', color: 'var(--muted)', marginTop: '4px', textTransform: 'uppercase' as const, letterSpacing: '0.5px' },
  input: { width: '100%', padding: '12px 14px', background: 'rgba(0, 0, 0, 0.3)', border: '1px solid rgba(255, 255, 255, 0.15)', borderRadius: '10px', color: 'var(--text)', fontSize: '13px', fontFamily: 'monospace', outline: 'none' },
  button: { padding: '12px 20px', background: ICP_COLORS.gradient, color: 'white', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s' },
  buttonSecondary: { padding: '12px 20px', background: 'rgba(255, 255, 255, 0.1)', color: 'var(--text)', border: '1px solid rgba(255, 255, 255, 0.15)', borderRadius: '10px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' },
  tabContainer: { display: 'flex', gap: '4px', padding: '4px', background: 'rgba(0, 0, 0, 0.3)', borderRadius: '14px', width: 'fit-content', marginBottom: '24px' },
  tab: (isActive: boolean) => ({ padding: '10px 18px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.3s ease', background: isActive ? ICP_COLORS.gradient : 'transparent', color: isActive ? 'white' : 'var(--muted)' }),
  emptyState: { textAlign: 'center' as const, padding: '60px 20px' },
};

function NetworkStatsBanner({ icpPrice, totalNeurons, totalAccounts, averageAPY, isLoading }: { icpPrice: number; totalNeurons: number; totalAccounts: number; averageAPY: number; isLoading: boolean }) {
  return (
    <div style={{ ...s.card, background: ICP_COLORS.lightGradient, border: '1px solid rgba(41, 171, 226, 0.25)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>Internet Computer</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
            <span style={{ fontSize: '28px', fontWeight: 700, color: ICP_COLORS.primary }}>${icpPrice.toFixed(2)}</span>
            <span style={{ fontSize: '13px', color: 'var(--muted)' }}>ICP</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '18px', fontWeight: 600, color: ICP_COLORS.secondary }}>{totalNeurons}</div>
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>Neurons</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '18px', fontWeight: 600 }}>{totalAccounts}</div>
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>Accounts</div>
          </div>
          {totalNeurons > 0 && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '18px', fontWeight: 600, color: '#22c55e' }}>{(averageAPY * 100).toFixed(1)}%</div>
              <div style={{ fontSize: '10px', color: 'var(--muted)' }}>Est. APY</div>
            </div>
          )}
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: isLoading ? ICP_COLORS.warning : ICP_COLORS.success, boxShadow: `0 0 10px ${isLoading ? ICP_COLORS.warning : ICP_COLORS.success}`, margin: '0 auto 4px' }} />
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>{isLoading ? 'Loading...' : 'Connected'}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatsOverview({ totalBalance, totalBalanceUsd, totalStaked, totalStakedUsd, totalMaturity, totalNeurons, totalVotingPower }: any) {
  const stats = [
    { label: 'Total Balance', value: `${totalBalance.toLocaleString(undefined, { maximumFractionDigits: 2 })} ICP`, subvalue: `$${totalBalanceUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}`, color: ICP_COLORS.primary, icon: <Icons.Wallet size={16} /> },
    { label: 'Staked in Neurons', value: `${totalStaked.toLocaleString(undefined, { maximumFractionDigits: 2 })} ICP`, subvalue: `$${totalStakedUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}`, color: ICP_COLORS.secondary, icon: <Icons.Lock size={16} /> },
    { label: 'Maturity', value: `${totalMaturity.toLocaleString(undefined, { maximumFractionDigits: 2 })} ICP`, subvalue: `${totalNeurons} neurons`, color: ICP_COLORS.success, icon: <Icons.TrendingUp size={16} /> },
    { label: 'Voting Power', value: totalVotingPower.toLocaleString(undefined, { maximumFractionDigits: 0 }), subvalue: 'NNS governance', color: ICP_COLORS.accent, icon: <Icons.Users size={16} /> },
  ];
  return (
    <div style={s.statsGrid}>
      {stats.map((stat, idx) => (
        <div key={idx} style={s.statCard(stat.color)}>
          <div style={{ color: stat.color, marginBottom: '8px' }}>{stat.icon}</div>
          <div style={{ ...s.statValue, color: stat.color }}>{stat.value}</div>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>{stat.subvalue}</div>
          <div style={s.statLabel}>{stat.label}</div>
        </div>
      ))}
    </div>
  );
}

function NeuronCard({ neuron, icpPrice, onRemove }: { neuron: ICPNeuronSummary; icpPrice: number; onRemove: () => void }) {
  const formatDuration = (seconds: number): string => {
    const days = Math.floor(seconds / 86400);
    const years = Math.floor(days / 365);
    const remainingDays = days % 365;
    if (years > 0) return `${years}y ${remainingDays}d`;
    return `${days}d`;
  };

  const stateColors: Record<string, string> = {
    locked: ICP_COLORS.secondary,
    dissolving: ICP_COLORS.warning,
    dissolved: ICP_COLORS.success,
  };

  const stateLabels: Record<string, string> = {
    locked: 'Locked',
    dissolving: 'Dissolving',
    dissolved: 'Dissolved',
  };

  return (
    <div style={{ ...s.card, background: 'rgba(82, 39, 133, 0.08)', border: '1px solid rgba(82, 39, 133, 0.2)', marginBottom: '12px', padding: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: stateColors[neuron.state] + '20', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icons.Lock size={18} color={stateColors[neuron.state]} />
          </div>
          <div>
            <div style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 600 }}>Neuron #{neuron.id}</div>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', background: stateColors[neuron.state] + '20', color: stateColors[neuron.state], borderRadius: '4px', fontSize: '10px', fontWeight: 600, marginTop: '4px' }}>
              {stateLabels[neuron.state]}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '18px', fontWeight: 700, color: ICP_COLORS.secondary }}>{neuron.stake.toLocaleString(undefined, { maximumFractionDigits: 2 })} ICP</div>
            <div style={{ fontSize: '11px', color: ICP_COLORS.success }}>${neuron.stakeUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
          </div>
          <button onClick={onRemove} style={{ background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', border: 'none', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer' }}><Icons.Trash2 size={12} /></button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', paddingTop: '12px', borderTop: '1px solid rgba(255, 255, 255, 0.06)' }}>
        <div>
          <div style={{ fontSize: '10px', color: 'var(--muted)', marginBottom: '2px' }}>Dissolve Delay</div>
          <div style={{ fontSize: '13px', fontWeight: 600 }}>{formatDuration(neuron.dissolveDelay)}</div>
        </div>
        <div>
          <div style={{ fontSize: '10px', color: 'var(--muted)', marginBottom: '2px' }}>Est. APY</div>
          <div style={{ fontSize: '13px', fontWeight: 600, color: '#22c55e' }}>{((neuron.estimatedAPY || 0) * 100).toFixed(1)}%</div>
        </div>
        <div>
          <div style={{ fontSize: '10px', color: 'var(--muted)', marginBottom: '2px' }}>Maturity</div>
          <div style={{ fontSize: '13px', fontWeight: 600, color: ICP_COLORS.success }}>{neuron.maturity.toLocaleString(undefined, { maximumFractionDigits: 2 })} ICP</div>
        </div>
        <div>
          <div style={{ fontSize: '10px', color: 'var(--muted)', marginBottom: '2px' }}>Voting Power</div>
          <div style={{ fontSize: '13px', fontWeight: 600 }}>{neuron.votingPower.toLocaleString()}</div>
        </div>
        <div>
          <div style={{ fontSize: '10px', color: 'var(--muted)', marginBottom: '2px' }}>Age</div>
          <div style={{ fontSize: '13px', fontWeight: 600 }}>{formatDuration(neuron.age)}</div>
        </div>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <NetworkMonitorEmptyState
      icon={<Icons.Icp size={40} color="white" />}
      title="Welcome to ICP Monitor"
      description="Track your Internet Computer neurons and account balances."
      colors={ICP_COLORS}
    >
      {/* ICP-specific second paragraph: explain the two input formats. */}
      <p style={{ color: 'var(--muted)', fontSize: '13px', maxWidth: '500px', margin: '0 auto', lineHeight: 1.6 }}>
        Add your <strong style={{ color: ICP_COLORS.secondary }}>Neuron ID</strong> (numeric, from NNS dApp) to track staking,
        or your <strong style={{ color: ICP_COLORS.primary }}>Account ID</strong> (64 hex chars) for balance.
      </p>
    </NetworkMonitorEmptyState>
  );
}

export function IcpMonitor() {
  const toast = useToast();
  const { wallets: storedItems, setWallets: setStoredItems } = useIcpMonitorWallets();
  const [newInput, setNewInput] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('neurons');

  // Separate neuron IDs and account IDs
  const neuronIds = storedItems.filter(item => isValidNeuronId(item));
  const accountIds = storedItems.filter(item => isValidIcpAccountId(item));

  const { wallets, neurons, networkStats, isLoading, error, refetch, icpPrice, totalBalance, totalBalanceUsd, totalStaked, totalStakedUsd, totalMaturity, totalVotingPower, totalNeurons, averageAPY } = useIcpData(accountIds, neuronIds);

  const handleAddItem = () => {
    const trimmed = newInput.trim();
    if (!trimmed) {
      toast('Please enter a Neuron ID or Account ID', 'warning', 3000);
      return;
    }

    const parsed = parseIcpInput(trimmed);

    if (parsed.type === 'unknown') {
      toast('Invalid format. Enter a Neuron ID (numeric) or Account ID (64 hex chars)', 'error', 4000);
      return;
    }

    if (storedItems.includes(trimmed)) {
      toast('This item is already added', 'warning', 3000);
      return;
    }

    setStoredItems([...storedItems, trimmed]);
    setNewInput('');
    setShowAddForm(false);

    if (parsed.type === 'neuronId') {
      toast('Neuron ID added - loading staking data', 'success', 3000);
    } else {
      toast('Account ID added - loading balance', 'success', 3000);
    }
  };

  const handleRemoveItem = (id: string) => {
    if (confirm(`Remove ${id.length > 20 ? id.slice(0, 10) + '...' : id}?`)) {
      setStoredItems(storedItems.filter(item => item !== id));
      toast('Item removed', 'info', 3000);
    }
  };

  const handleRemoveNeuron = (neuronId: number) => {
    const id = neuronId.toString();
    handleRemoveItem(id);
  };

  const parsed = parseIcpInput(newInput.trim());
  const isValidInput = parsed.type !== 'unknown' && newInput.trim().length > 0;

  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: 'neurons', label: `Neurons (${totalNeurons})`, icon: <Icons.Lock size={16} /> },
    { id: 'accounts', label: `Accounts (${wallets.length})`, icon: <Icons.Wallet size={16} /> },
  ];

  return (
    <div style={s.page}>
      <NetworkMonitorPageHeader
        icon={<Icons.Icp size={36} color={ICP_COLORS.primary} />}
        title="ICP Monitor"
        subtitle="Monitor your Internet Computer neurons and accounts"
        primaryAction={
          showAddForm
            ? {
                label: 'Cancel',
                icon: <Icons.X size={16} />,
                onClick: () => setShowAddForm(false),
                variant: 'secondary',
              }
            : {
                label: 'Add Neuron',
                icon: <Icons.Plus size={16} />,
                onClick: () => setShowAddForm(true),
              }
        }
        refreshAction={
          storedItems.length > 0
            ? {
                label: 'Refresh',
                icon: <Icons.RefreshCw size={16} />,
                onClick: () => refetch(),
              }
            : undefined
        }
        colors={ICP_COLORS}
      />
      {/* IcpMonitor keeps its custom add-form below — it has a chain-
          specific helper text + a type indicator (Neuron ID vs Account
          ID) that the simple <NetworkAddressInputForm> primitive
          doesn't model. */}

      {showAddForm && (
        <div style={{ ...s.card, background: 'rgba(82, 39, 133, 0.1)', border: '1px solid rgba(82, 39, 133, 0.3)' }}>
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '8px' }}>
              Enter your <strong style={{ color: ICP_COLORS.secondary }}>Neuron ID</strong> (numeric, from NNS dApp) or <strong style={{ color: ICP_COLORS.primary }}>Account ID</strong> (64 hex) for balance.
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <input
                type="text"
                value={newInput}
                onChange={(e) => setNewInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddItem()}
                placeholder="Neuron ID (e.g., 12345678901234567890) or Account ID (64 hex)"
                style={{ ...s.input, borderColor: newInput && !isValidInput ? '#ef4444' : 'rgba(255, 255, 255, 0.15)' }}
                autoFocus
              />
              {newInput && (
                <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {isValidInput ? (
                    <>
                      <span style={{ fontSize: '10px', color: parsed.type === 'neuronId' ? ICP_COLORS.secondary : ICP_COLORS.primary }}>{parsed.type === 'neuronId' ? 'Neuron ID' : 'Account ID'}</span>
                      <Icons.Check size={16} color={ICP_COLORS.success} />
                    </>
                  ) : (
                    <Icons.X size={16} color="#ef4444" />
                  )}
                </span>
              )}
            </div>
            <button onClick={handleAddItem} disabled={!isValidInput} style={{ ...s.button, opacity: isValidInput ? 1 : 0.5, cursor: isValidInput ? 'pointer' : 'not-allowed' }}><Icons.Check size={16} />Add</button>
          </div>
        </div>
      )}

      <NetworkStatsBanner icpPrice={icpPrice} totalNeurons={totalNeurons} totalAccounts={wallets.length} averageAPY={averageAPY} isLoading={isLoading} />

      {/* V2.K.3 — ICP supply breakdown (circulating, NNS-staked,
          free float, inflation + Mission 70 progress). Fed by
          CoinGecko, refreshes every 5 min. */}
      <IcpSupplyBreakdown />

      {/* Blockchain Health Metrics */}
      <IcpHealthMetrics />

      {error && <div style={{ ...s.card, background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)' }}><div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#ef4444' }}><Icons.AlertTriangle size={20} /><span>Error: {(error as Error)?.message}</span></div></div>}

      {storedItems.length > 0 ? (
        <>
          <StatsOverview totalBalance={totalBalance} totalBalanceUsd={totalBalanceUsd} totalStaked={totalStaked} totalStakedUsd={totalStakedUsd} totalMaturity={totalMaturity} totalNeurons={totalNeurons} totalVotingPower={totalVotingPower} />
          <MonitorTabs<TabType>
            tabs={tabs}
            activeTab={activeTab}
            onChange={setActiveTab}
            colors={ICP_COLORS}
            ariaLabel="ICP monitor tabs"
          />

          {activeTab === 'neurons' && (
            <div>
              <h2 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}><Icons.Lock size={18} /> Staked Neurons ({neurons.length})</h2>
              {isLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}><Icons.Activity size={32} style={{ opacity: 0.3 }} /></div>
              ) : neurons.length > 0 ? (
                neurons.map(neuron => (
                  <NeuronCard key={neuron.id} neuron={neuron} icpPrice={icpPrice} onRemove={() => handleRemoveNeuron(neuron.id)} />
                ))
              ) : (
                <div style={{ ...s.card, textAlign: 'center', padding: '40px', color: 'var(--muted)' }}>
                  <Icons.Lock size={32} style={{ opacity: 0.3, marginBottom: '12px' }} />
                  <p>No neurons tracked yet.</p>
                  <p style={{ fontSize: '12px', marginTop: '8px' }}>Add your Neuron ID from the NNS dApp to track staking.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'accounts' && (
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}><Icons.Wallet size={18} /> Account Balances ({wallets.length})</h3>
              {isLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}><Icons.Activity size={32} style={{ opacity: 0.3 }} /></div>
              ) : wallets.length > 0 ? (
                wallets.map(wallet => (
                  <div key={wallet.accountIdentifier} style={{ ...s.card, background: 'rgba(41, 171, 226, 0.08)', border: '1px solid rgba(41, 171, 226, 0.2)', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: ICP_COLORS.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Icons.Icp size={20} color="white" />
                        </div>
                        <div>
                          <div style={{ fontFamily: 'monospace', fontSize: '11px', fontWeight: 600 }}>{wallet.accountIdentifier.slice(0, 12)}...{wallet.accountIdentifier.slice(-8)}</div>
                          <span style={{ display: 'inline-flex', padding: '2px 8px', background: 'rgba(41, 171, 226, 0.15)', color: ICP_COLORS.primary, borderRadius: '4px', fontSize: '10px', fontWeight: 600 }}>Account</span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '18px', fontWeight: 700 }}>{wallet.balance.toLocaleString(undefined, { maximumFractionDigits: 2 })} ICP</div>
                          <div style={{ fontSize: '12px', color: ICP_COLORS.success }}>${wallet.balanceUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                        </div>
                        <button onClick={() => handleRemoveItem(wallet.accountIdentifier)} style={{ background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', border: 'none', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer' }}><Icons.Trash2 size={12} /></button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ ...s.card, textAlign: 'center', padding: '40px', color: 'var(--muted)' }}>
                  <Icons.Wallet size={32} style={{ opacity: 0.3, marginBottom: '12px' }} />
                  <p>No accounts tracked yet.</p>
                  <p style={{ fontSize: '12px', marginTop: '8px' }}>Add an Account ID (64 hex chars) to track balance.</p>
                </div>
              )}
            </div>
          )}
        </>
      ) : <EmptyState />}

      <div style={{ marginTop: '30px', padding: '16px 20px', background: 'rgba(82, 39, 133, 0.08)', borderRadius: '12px', border: '1px solid rgba(82, 39, 133, 0.15)', fontSize: '12px', color: 'var(--muted)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}><Icons.Info size={14} style={{ color: ICP_COLORS.secondary }} /><strong style={{ color: 'var(--text)' }}>How to find your Neuron ID</strong></div>
        <p style={{ lineHeight: 1.6 }}>
          1. Go to <strong>nns.ic0.app</strong> and connect your wallet<br/>
          2. Click on your neuron in the list<br/>
          3. Copy the <strong>Neuron ID</strong> (numeric string like 12345678901234567890)
        </p>
      </div>
    </div>
  );
}
